import { useEffect } from "react";
import { useSiteContent } from "../../context/SiteContent";

function upsertMeta(attr, key, value) {
  if (!value) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function injectScript(id, src, inner) {
  if (document.getElementById(id)) return;
  const s = document.createElement("script");
  s.id = id;
  if (src) { s.src = src; s.async = true; }
  if (inner) s.innerHTML = inner;
  document.head.appendChild(s);
}

// Injects SEO meta, JSON-LD schema, favicon, GA4/GTM/GSC based on CMS content.
export const SeoAnalytics = () => {
  const { content } = useSiteContent();

  useEffect(() => {
    if (!content) return;
    const ci = content.companyInfo || {};
    const social = content.socialLinks || {};
    const track = content.analyticsAndTracking || {};

    document.title = `${ci.legalName || "Helios Cleantech"} — Autonomous Solar Cleaning Robotics & O&M`;
    upsertMeta("name", "description",
      "Helios Cleantech engineers autonomous, waterless solar cleaning robots (Yantr 2.0 & 3.0) and comprehensive O&M automation for utility-scale solar farms and commercial rooftops.");
    upsertMeta("property", "og:title", `${ci.legalName} — Solar Cleaning Robotics & O&M`);
    upsertMeta("property", "og:description", "Eliminate up to 99.5% soiling loss with intelligent, waterless robotics.");
    upsertMeta("property", "og:type", "website");
    if (content.galleryMedia?.heroBgImageUrl) upsertMeta("property", "og:image", content.galleryMedia.heroBgImageUrl);

    if (track.gscVerificationTag) upsertMeta("name", "google-site-verification", track.gscVerificationTag);

    // Favicon
    if (ci.faviconUrl) {
      let link = document.querySelector("link[rel='icon']");
      if (!link) { link = document.createElement("link"); link.rel = "icon"; document.head.appendChild(link); }
      link.href = ci.faviconUrl;
    }

    // JSON-LD Organization + Products
    const products = (content.productSeries || []).flatMap((s) =>
      (s.models || []).filter((m) => m.isActive).map((m) => ({
        "@type": "Product",
        name: m.name,
        description: m.tagline,
        image: m.imageUrl || undefined,
        brand: { "@type": "Brand", name: "Helios Cleantech" },
        offers: { "@type": "Offer", priceCurrency: "INR", price: m.startingPrice, availability: "https://schema.org/InStock" },
      }))
    );
    const jsonld = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          name: ci.legalName,
          email: ci.email,
          address: { "@type": "PostalAddress", streetAddress: ci.worksAddress, addressCountry: "IN" },
          telephone: ci.contact1Phone,
          sameAs: [social.instagram, social.facebook, social.linkedin, social.twitter, social.indiamart].filter(Boolean),
          taxID: ci.gstNumber,
        },
        ...products,
        {
          "@type": "FAQPage",
          mainEntity: (content.faqs || []).map((f) => ({
            "@type": "Question", name: f.question,
            acceptedAnswer: { "@type": "Answer", text: f.answer },
          })),
        },
      ],
    };
    let ld = document.getElementById("helios-jsonld");
    if (!ld) { ld = document.createElement("script"); ld.id = "helios-jsonld"; ld.type = "application/ld+json"; document.head.appendChild(ld); }
    ld.textContent = JSON.stringify(jsonld);

    // GA4
    if (track.ga4MeasurementId) {
      injectScript("ga4-src", `https://www.googletagmanager.com/gtag/js?id=${track.ga4MeasurementId}`);
      injectScript("ga4-init", null,
        `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${track.ga4MeasurementId}');`);
    }
    // GTM
    if (track.gtmContainerId) {
      injectScript("gtm-init", null,
        `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${track.gtmContainerId}');`);
    }
  }, [content]);

  return null;
};
